import {type PayPalNamespace, loadScript} from '@paypal/paypal-js';
import {disableGatewayOption, observeCheckoutHTML, onCheckoutGatewayChange} from '../checkout';
import {Feature} from '../features';
import {PayPalVenmoPayment} from './class-venmo-payment';
import {type AbstractPayPalPayment, type PayPalInstance} from './abstract/class-abstract-paypal-payment';
import {PayPalWalletPayment} from './class-paypal-wallet-payment';
import {PayPalPayLaterPayment} from './class-paypal-paylater-payment';
import {PayPalCreditPayment} from './class-paypal-credit-payment';
import {$qs} from '../../../@shared/ts/dom';
import {FeatureFlag} from '../../../@type/features';
import {PayPalCardPayment} from './class-paypal-card-payment';
import {registerUpdatedCheckoutHandler, registerGatewaySelectedHandler, registerCheckoutSubmitHandler, createNativeCheckoutPaymentAttempt} from './checkout';
import {type PeachPayCheckoutData} from '../types';

declare const peachpay_checkout_data: PeachPayCheckoutData;

async function initPayPalPayments(): Promise<void> {
	if (!Feature.enabled(FeatureFlag.PAYPAL_GATEWAYS)) {
		return;
	}

	const clientId = Feature.metadata<string>(FeatureFlag.PAYPAL_GATEWAYS, 'client_id');
	const merchantId = Feature.metadata<string>(FeatureFlag.PAYPAL_GATEWAYS, 'merchant_id');
	const partnerAttributionId = Feature.metadata<string>(FeatureFlag.PAYPAL_GATEWAYS, 'partner_attribution_id');
	const isSandbox = Feature.metadata<boolean>(FeatureFlag.PAYPAL_GATEWAYS, 'is_sandbox') ?? false;

	if (!clientId) {
		return;
	}

	const buttonPayments: AbstractPayPalPayment[] = [];
	const enabledFundingSources: string[] = [];

	const paymentGateways = [
		[FeatureFlag.PAYPAL_WALLET_GATEWAY, PayPalWalletPayment],
		[FeatureFlag.PAYPAL_VENMO_GATEWAY, PayPalVenmoPayment],
		[FeatureFlag.PAYPAL_PAYLATER_GATEWAY, PayPalPayLaterPayment],
		[FeatureFlag.PAYPAL_CREDIT_GATEWAY, PayPalCreditPayment],
	] as const;

	paymentGateways.forEach(([flag, PaymentClass]) => {
		if (Feature.enabled(flag)) {
			buttonPayments.push(new PaymentClass());
		}
	});

	if (Feature.enabled(FeatureFlag.PAYPAL_VENMO_GATEWAY)) {
		enabledFundingSources.push('venmo');
	}

	if (Feature.enabled(FeatureFlag.PAYPAL_PAYLATER_GATEWAY)) {
		enabledFundingSources.push('paylater');
	}

	if (Feature.enabled(FeatureFlag.PAYPAL_CREDIT_GATEWAY)) {
		enabledFundingSources.push('credit');
	}

	const hidePayPalButtons = () => {
		buttonPayments.forEach(p => {
			p.unmount();
		});
	};

	const setGatewayOptionPendingState = (gatewayId: string, isPending: boolean) => {
		const option = document.querySelector<HTMLElement>('li.wc_payment_method.payment_method_' + gatewayId);
		if (!option) {
			return;
		}

		option.classList.toggle('hide', isPending);
	};

	const hideWCButton = () => $qs('#place_order', $el => {
		$el.style.setProperty('display', 'none', 'important');
	});
	const showWCButton = () => $qs('#place_order', $el => {
		$el.style.removeProperty('display');
	});
	const syncSelectedPayPalButton = (gatewayId: string) => {
		const payment = buttonPayments.find(p => p.gatewayId() === gatewayId);

		hidePayPalButtons();

		if (!payment) {
			showWCButton();
			return;
		}

		hideWCButton();
		payment.mount();
	};

	// Hide or display currently selected PayPal method buttons.
	await onCheckoutGatewayChange((gatewayId: string) => {
		syncSelectedPayPalButton(gatewayId);
	});

	buttonPayments.forEach(payment => {
		setGatewayOptionPendingState(payment.gatewayId(), true);

		registerUpdatedCheckoutHandler(payment.gatewayId(), () => {
			syncSelectedPayPalButton(payment.gatewayId());
		});
	});

	const loadOptions: Parameters<typeof loadScript>[0] = {
		clientId,
		merchantId: merchantId ?? undefined,
		dataPartnerAttributionId: partnerAttributionId ?? undefined,
		currency: peachpay_checkout_data.cart_details.currency,
		dataPageType: 'checkout',
		components: ['buttons', 'card-fields'],
	};

	if (enabledFundingSources.length > 0) {
		loadOptions.enableFunding = enabledFundingSources;
	}

	if (isSandbox) {
		loadOptions.buyerCountry = 'US';
	}

	const paypal = await loadScript(loadOptions)
		.then((paypal: PayPalNamespace) => paypal as unknown as PayPalInstance)
		.catch((error: unknown) => {
			console.error('[PeachPay PayPal] failed to load the PayPal JS SDK script', error);
			return null;
		});

	if (!paypal) {
		return;
	}

	if (Feature.enabled(FeatureFlag.PAYPAL_CARD_GATEWAY)) {
		try {
			const cardPayments = new PayPalCardPayment(paypal);
			if (cardPayments.isEligible()) {
				const syncPayPalCardState = () => {
					showWCButton();

					if (!cardPayments.isMounted()) {
						cardPayments.mount();
					}
				};

				registerGatewaySelectedHandler(cardPayments.gatewayId(), () => {
					syncPayPalCardState();
				});

				registerUpdatedCheckoutHandler(cardPayments.gatewayId(), () => {
					syncPayPalCardState();
				});

				registerCheckoutSubmitHandler(cardPayments.gatewayId(), () => {
					cardPayments.submit(createNativeCheckoutPaymentAttempt());
				});
			} else {
				observeCheckoutHTML(disableGatewayOption(cardPayments.gatewayId()));
			}
		} catch (error: unknown) {
			console.error('PayPal Advanced cards: ', error);
		}
	}

	await Promise.all(buttonPayments.map(async p => p.init(paypal)));

	const currentlySelectedGatewayId = (document.querySelector<HTMLInputElement>('input[name="payment_method"]:checked')?.value ?? '');
	syncSelectedPayPalButton(currentlySelectedGatewayId);
}

export {initPayPalPayments};
